/**
 * session_resume — RECONNECT = RELOAD + RESUME (2026-09-02).
 *
 * A network blip (Chrome's ERR_NETWORK_CHANGED: Wi-Fi roam, VPN toggle, the
 * WSL adapter resetting, sleep/wake) drops the SpacetimeDB socket. The SDK
 * cannot reopen a socket inside an existing DbConnection (its own manager
 * builds a new one), and this client binds table callbacks ONCE per
 * connection across dozens of modules, so a new connection object would leave
 * them on the dead one and re-deliver every snapshot row into handlers that
 * create scene/DOM objects. The robust path is the one the dev server already
 * forces: reload the page — but RESUME straight into the world instead of
 * landing on the menu. The network module keeps this record fresh while
 * in-world; the boot skips the menu + traveler screens when it is recent.
 * Pure over an injectable storage so the harness pins it.
 */
export interface ResumeRecord { worldId: string; x: number; z: number; at: number }

export const RESUME_KEY = 'atlas:resume';
/** A record older than this is stale (a real fresh visit, not a reload). */
export const RESUME_MAX_AGE_MS = 120_000;
/** Reconnect polling: 1 s, 2 s, 4 s … capped. */
export const RECONNECT_BASE_MS = 1000;
export const RECONNECT_CAP_MS = 15_000;
/** Stop polling after this long and leave a "reload to reconnect" status. */
export const RECONNECT_GIVE_UP_MS = 5 * 60_000;

export interface KeyValueStore { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

function defaultStore(): KeyValueStore | null {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
}

export function markResume(rec: { worldId: bigint | string; x: number; z: number }, now = Date.now(), store: KeyValueStore | null = defaultStore()): void {
  if (!store) return;
  try { store.setItem(RESUME_KEY, JSON.stringify({ worldId: String(rec.worldId), x: rec.x, z: rec.z, at: now })); } catch { /* storage full / blocked */ }
}

/** Future-dated records tolerated (a backward clock step during the reload). */
export const RESUME_CLOCK_SKEW_MS = 10_000;

/** The resume record if it is fresh (≤ maxAge), else null. Never throws. */
export function pendingResume(now = Date.now(), maxAgeMs = RESUME_MAX_AGE_MS, store: KeyValueStore | null = defaultStore()): ResumeRecord | null {
  if (!store) return null;
  try {
    const raw = store.getItem(RESUME_KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as Partial<ResumeRecord>;
    if (typeof r.worldId !== 'string' || typeof r.at !== 'number' || !/^\d+$/.test(r.worldId)) return null;
    // A clock stepped BACK (this WSL2 box steps ~2.5 s every ~32 s) put `now` before the record and
    // turned a reconnect into a menu landing — allow a little skew (bug sweep 2026-09-22).
    if (now - r.at > maxAgeMs || r.at - now > RESUME_CLOCK_SKEW_MS) return null;
    return { worldId: r.worldId, x: Number(r.x ?? 0), z: Number(r.z ?? 0), at: r.at };
  } catch { return null; }
}

export function clearResume(store: KeyValueStore | null = defaultStore()): void {
  try { store?.removeItem(RESUME_KEY); } catch { /* ignore */ }
}

/** Delay before reconnect attempt `attempt` (1-based): exponential, capped. */
export function reconnectDelayMs(attempt: number): number {
  return Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** Math.max(0, attempt - 1));
}

/** The server's HTTP ping for the WS uri the client uses (wss://host/ → https://host/v1/ping). */
export function serverPingUrl(wsUri: string): string {
  return wsUri.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:').replace(/\/+$/, '') + '/v1/ping';
}
